const CHAT_SCROLL_STORAGE_KEY = "lawworks.ai-chat.scroll-positions.v1";
const MAX_SAVED_THREADS = 100;
const MAX_ID_LENGTH = 128;
const MAX_VIEWPORT_OFFSET = 1_000_000;

export type ChatScrollPosition = {
  atBottom: boolean;
  itemId: string;
  revision: number;
  savedAt: number;
  viewportOffset: number;
};

export type ChatScrollPositionInput = Omit<ChatScrollPosition, "savedAt">;

type StoredChatScrollPosition = ChatScrollPosition & {
  threadId: string;
};

type ChatScrollPositionStore = {
  entries: StoredChatScrollPosition[];
  version: 1;
};

type CaptureChatScrollPositionInput = {
  atBottomDistance?: number;
  container: HTMLElement;
  revision: number;
  threadId: string;
};

function storageAvailable() {
  return typeof window !== "undefined";
}

function validId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= MAX_ID_LENGTH
  );
}

function validPosition(value: unknown): value is StoredChatScrollPosition {
  if (!value || typeof value !== "object") return false;
  const position = value as Partial<StoredChatScrollPosition>;
  return (
    validId(position.threadId) &&
    validId(position.itemId) &&
    typeof position.atBottom === "boolean" &&
    Number.isInteger(position.revision) &&
    Number(position.revision) >= 0 &&
    Number.isFinite(position.savedAt) &&
    Number(position.savedAt) > 0 &&
    Number.isFinite(position.viewportOffset) &&
    Math.abs(Number(position.viewportOffset)) <= MAX_VIEWPORT_OFFSET
  );
}

function readStore(): StoredChatScrollPosition[] {
  if (!storageAvailable()) return [];
  try {
    const serialized = window.sessionStorage.getItem(CHAT_SCROLL_STORAGE_KEY);
    if (!serialized) return [];
    const store = JSON.parse(serialized) as Partial<ChatScrollPositionStore>;
    if (store.version !== 1 || !Array.isArray(store.entries)) return [];
    return store.entries.filter(validPosition).slice(0, MAX_SAVED_THREADS);
  } catch {
    return [];
  }
}

function writeStore(entries: StoredChatScrollPosition[]) {
  if (!storageAvailable()) return;
  const store: ChatScrollPositionStore = {
    entries: entries.slice(0, MAX_SAVED_THREADS),
    version: 1,
  };
  try {
    window.sessionStorage.setItem(
      CHAT_SCROLL_STORAGE_KEY,
      JSON.stringify(store),
    );
  } catch {
    // Scroll restoration is best-effort when browser storage is unavailable.
  }
}

export function readChatScrollPosition(
  threadId: string,
): ChatScrollPosition | null {
  if (!validId(threadId)) return null;
  const position = readStore().find((entry) => entry.threadId === threadId);
  if (!position) return null;
  return {
    atBottom: position.atBottom,
    itemId: position.itemId,
    revision: position.revision,
    savedAt: position.savedAt,
    viewportOffset: position.viewportOffset,
  };
}

export function writeChatScrollPosition(
  threadId: string,
  position: ChatScrollPositionInput,
): ChatScrollPosition | null {
  if (
    !validId(threadId) ||
    !validId(position.itemId) ||
    !Number.isInteger(position.revision) ||
    position.revision < 0 ||
    !Number.isFinite(position.viewportOffset)
  ) {
    return null;
  }

  const savedPosition: StoredChatScrollPosition = {
    atBottom: position.atBottom,
    itemId: position.itemId,
    revision: position.revision,
    savedAt: Date.now(),
    threadId,
    viewportOffset: Math.max(
      -MAX_VIEWPORT_OFFSET,
      Math.min(MAX_VIEWPORT_OFFSET, position.viewportOffset),
    ),
  };
  const remaining = readStore().filter((entry) => entry.threadId !== threadId);
  writeStore([savedPosition, ...remaining]);
  return {
    atBottom: savedPosition.atBottom,
    itemId: savedPosition.itemId,
    revision: savedPosition.revision,
    savedAt: savedPosition.savedAt,
    viewportOffset: savedPosition.viewportOffset,
  };
}

export function removeChatScrollPosition(threadId: string) {
  if (!validId(threadId)) return;
  writeStore(readStore().filter((entry) => entry.threadId !== threadId));
}

export function captureChatScrollPosition({
  atBottomDistance = 96,
  container,
  revision,
  threadId,
}: CaptureChatScrollPositionInput): ChatScrollPosition | null {
  const containerRect = container.getBoundingClientRect();
  const renderedRows = [
    ...container.querySelectorAll<HTMLElement>("[data-timeline-item-ids]"),
  ];
  const anchor = renderedRows.find((row) => {
    const rowRect = row.getBoundingClientRect();
    return (
      rowRect.bottom > containerRect.top && rowRect.top < containerRect.bottom
    );
  });
  const itemId = anchor?.dataset.timelineItemIds?.split(" ").find(Boolean);
  if (!anchor || !itemId) return null;

  const viewportOffset = anchor.getBoundingClientRect().top - containerRect.top;
  const atBottom =
    container.scrollHeight - container.scrollTop - container.clientHeight <=
    atBottomDistance;
  return writeChatScrollPosition(threadId, {
    atBottom,
    itemId,
    revision,
    viewportOffset,
  });
}

export function visibleChatAnchor(element: HTMLDivElement) {
  const containerTop = element.getBoundingClientRect().top;
  const renderedRows = [
    ...element.querySelectorAll<HTMLElement>("[data-timeline-item-ids]"),
  ];
  const renderedRow = renderedRows.find(
    (candidate) => candidate.getBoundingClientRect().bottom > containerTop,
  );
  const itemId = renderedRow?.dataset.timelineItemIds?.split(" ")[0];
  return renderedRow && itemId
    ? {
        itemId,
        viewportOffset: renderedRow.getBoundingClientRect().top - containerTop,
      }
    : undefined;
}
